import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeftRight,
  ArrowRight,
  BarChart3,
  Brain,
  CheckCircle,
  CircleCheck,
  ClipboardCheck,
  Download,
  FileText,
  Folder,
  HomeIcon,
  ImageOff,
  LayoutDashboard,
  List,
  Loader2,
  LockKeyhole,
  MessageCircle,
  Network,
  Play,
  Puzzle,
  Scale,
  Shield,
  ShieldAlert,
  Sparkles,
  Star,
  Target,
  Clock,
  Users,
  Zap,
} from "lucide-react";
import claudeIcon from "@/assets/integrations/claude.png";
import { loadLensJsonProperties } from "@/lib/lens-json-properties";
import { expertMarqueeDurationSeconds } from "@/lib/expert-marquee";
import notionIcon from "@/assets/integrations/notion.png";
import openclawIcon from "@/assets/integrations/openclaw.png";
import microsoft365Icon from "@/assets/integrations/microsoft365.png";
import slackIcon from "@/assets/integrations/slack.svg";
import n8nIcon from "@/assets/integrations/n8n.png";
import homeApp1 from "@/assets/home-apps/app1.png.asset.json";
import homeApp2 from "@/assets/home-apps/app2.png.asset.json";
import homeApp3 from "@/assets/home-apps/app3.png.asset.json";
import homeApp4 from "@/assets/home-apps/app4.png.asset.json";
import homeApp5 from "@/assets/home-apps/app5.png.asset.json";

// Lovable asset manifests contain root-relative delivery paths. Resolve them
// against the project host so the gallery also works in local and custom-domain
// environments, where Vite/the custom host does not serve /__l5e assets.
const LOVABLE_ASSET_ORIGIN = "https://applyt.lovable.app";

function resolveHomeAppAssetUrl(assetUrl: string) {
  return new URL(assetUrl, LOVABLE_ASSET_ORIGIN).toString();
}

const homeAppShowcase = [
  { src: resolveHomeAppAssetUrl(homeApp1.url), alt: "Pre-Seed MVP Raise App dashboard" },
  { src: resolveHomeAppAssetUrl(homeApp2.url), alt: "Micro-App Launch App dashboard" },
  { src: resolveHomeAppAssetUrl(homeApp3.url), alt: "Hiring Decision Matrix for engineers" },
  { src: resolveHomeAppAssetUrl(homeApp4.url), alt: "12-Week D.E.A.L. Sprint App" },
  { src: resolveHomeAppAssetUrl(homeApp5.url), alt: "Execution KPIs command center" },
];

type ShowcasePreview = { src: string | null; alt: string };
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Seo } from "@/components/seo/Seo";
import { Header } from "@/components/Header";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { trackEvent } from "@/lib/analytics";
import { getWizardResumeNavigation } from "@/lib/wizard";
import {
  copyTemplatePreviewLink,
  loadHomepageTemplateGallery,
  rememberAppTemplateContinuation,
  templateContinuationPath,
  type PublicAppTemplate,
} from "@/lib/app-templates";
import { createUuid } from "@/lib/uuid";
import { TemplatePreviewDialog } from "@/components/templates/TemplatePreviewDialog";
import { useToast } from "@/hooks/use-toast";

import { useAuth } from "@/components/auth/AuthContext";
import logo from "@/assets/logo.svg";
import { Footer } from "@/components/Footer";

// Fields the homepage actually reads from thinking_methods rows.
interface ThinkingMethodRow {
  id: string;
  name: string;
  author: string;
  bio: string;
  icon_url?: string;
  status: string;
  author_email?: string | null;
}

// Homepage display model: DB row plus lens metadata merged from JSON_PROPERTIES.
type ThinkingMethod = ThinkingMethodRow & {
  industry?: string;
  header?: string;
  teaser?: string;
  skills?: string[];
};

const THINKING_METHOD_HOME_PROJECTION = "id,name,author,bio,icon_url,author_email,status";

const outcomeSuggestions = ["Fundraising management", "Hiring operations", "MVP launch", "Customer onboarding"];

const HOME_H1_STYLE = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "clamp(2.35rem, min(4.9vw, 8svh), 4.75rem)",
};

const HOME_H2_STYLE = {
  fontFamily: "'Playfair Display', Georgia, serif",
};

async function gateRouterErrorMessage(error: unknown, fallback: string) {
  const context = error && typeof error === "object" && "context" in error
    ? (error as { context?: Response }).context
    : undefined;

  if (context) {
    try {
      const payload: unknown = await context.clone().json();
      if (payload && typeof payload === "object" && "error" in payload) {
        const message = (payload as { error?: unknown }).error;
        if (typeof message === "string" && message.trim()) return message;
      }
    } catch {
      // Fall back to the Supabase error message below.
    }
  }

  if (error instanceof Error && error.message !== "Edge Function returned a non-2xx status code") {
    return error.message;
  }
  return fallback;
}

function ContextMapPreview() {
  const rows = [
    { label: "Goal", icon: Target },
    { label: "Constraints", icon: LockKeyhole },
    { label: "Assets", icon: Folder },
    { label: "Risks", icon: ShieldAlert },
    { label: "Success Criteria", icon: Star },
  ];

  return (
    <div className="flex h-full items-center justify-center bg-[#f0f3f7] px-6 py-7" aria-hidden>
      <div className="w-full max-w-[278px] rounded-xl border border-[#58708d] bg-white px-5 py-4 shadow-[0_10px_24px_-18px_rgba(12,35,69,0.65)]">
        <div className="space-y-3.5">
          {rows.map(({ label, icon: Icon }, index) => (
            <div key={label} className="grid grid-cols-[16px_minmax(0,1fr)_76px] items-center gap-3">
              <Icon className="h-4 w-4 text-[#7188a8]" strokeWidth={1.8} />
              <span className="text-[11px] font-semibold text-[#30486c]">{label}</span>
              <span className="space-y-1">
                <span className="block h-1 rounded-full bg-[#dfe5ec]" />
                {index < 4 && <span className="block h-1 w-4/5 rounded-full bg-[#e9edf2]" />}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-[#dfe5ec] pt-3">
          <span className="mx-auto flex w-fit items-center gap-2 rounded-md bg-[#233f6d] px-4 py-1.5 text-[10px] font-bold text-white">
            <CircleCheck className="h-3.5 w-3.5" /> Apply
          </span>
        </div>
      </div>
    </div>
  );
}

function OperatingBlueprintPreview() {
  const nodes = [
    { label: "Questions", icon: MessageCircle },
    { label: "Decision Rules", icon: Scale },
    { label: "Workflow", icon: ArrowLeftRight },
    { label: "Checkpoints", icon: CircleCheck },
  ];

  return (
    <div className="flex h-full items-center justify-center bg-[#edf1f6] px-3 py-6" aria-hidden>
      <div className="w-full max-w-[350px] text-center">
        <div className="mx-auto w-fit rounded-lg bg-[#294670] px-4 py-2 text-[10px] font-bold text-white shadow-sm">
          Context / Goal
        </div>
        <div className="mx-auto h-5 w-px bg-[#536b89]" />
        <div className="mx-auto flex w-fit items-center gap-3 rounded-lg bg-[#173b68] px-5 py-2.5 text-[13px] font-bold text-white shadow-sm">
          <Network className="h-5 w-5" strokeWidth={1.8} /> AI Generation
        </div>
        <div className="relative mx-auto h-6 w-[76%] border-x border-t border-[#71839b]">
          <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-[#71839b]" />
        </div>
        <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
          {nodes.map(({ label, icon: Icon }) => (
            <div key={label} className="flex min-h-16 flex-col items-center justify-center rounded-lg border border-[#acb8c9] bg-white/60 px-1.5 py-2 text-[#344f73]">
              <Icon className="h-4 w-4" strokeWidth={1.7} />
              <span className="mt-1 text-[9px] font-semibold leading-3">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ExecutionWorkspacePreview() {
  return (
    <div className="flex h-full items-center justify-center bg-[#faf4eb] px-5 py-7" aria-hidden>
      <div className="flex w-full max-w-[320px] overflow-hidden rounded-xl border border-[#d9dee6] bg-white shadow-[0_10px_24px_-18px_rgba(12,35,69,0.65)]">
        <div className="w-10 shrink-0 border-r border-[#e1e5eb] bg-[#f5f7f9]">
          <div className="flex h-10 items-center justify-center bg-[#17254a] text-[12px] font-bold text-white">A</div>
          <div className="flex flex-col items-center gap-4 py-4 text-[#6f85a2]">
            <HomeIcon className="h-3.5 w-3.5" />
            <List className="h-3.5 w-3.5" />
            <ClipboardCheck className="h-3.5 w-3.5" />
            <BarChart3 className="h-3.5 w-3.5" />
          </div>
        </div>
        <div className="grid flex-1 grid-cols-2 gap-2 p-3">
          <div className="rounded-lg border border-[#dfe4ea] p-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[9px] font-bold text-[#263958]">Next Action</span>
              <span className="h-3 w-3 rounded-full border border-[#e4bd86]" />
            </div>
            <span className="mt-2 block h-1 w-4/5 rounded-full bg-[#e7ebef]" />
            <span className="mt-1 block h-1 w-3/5 rounded-full bg-[#e7ebef]" />
            <span className="mt-3 inline-block rounded-full bg-[#2f6ca0] px-3 py-1 text-[8px] font-bold text-white">Start</span>
          </div>
          <div className="rounded-lg border border-[#dfe4ea] p-2.5">
            <span className="text-[9px] font-bold text-[#263958]">Decision Log</span>
            {[0, 1, 2].map((item) => (
              <div key={item} className="mt-2 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#edcb98]" />
                <span className="h-1 w-4/5 rounded-full bg-[#e7ebef]" />
              </div>
            ))}
            <span className="mt-2 block text-right text-[7px] font-semibold text-[#5881a7]">View all</span>
          </div>
          <div className="rounded-lg border border-[#dfe4ea] p-2.5">
            <span className="text-[9px] font-bold text-[#263958]">Draft Output</span>
            <div className="mt-2 flex items-center gap-2">
              <FileText className="h-6 w-6 text-[#a9b5c3]" strokeWidth={1.6} />
              <span className="flex-1 space-y-1.5">
                <span className="block h-1 rounded-full bg-[#e7ebef]" />
                <span className="block h-1 w-4/5 rounded-full bg-[#e7ebef]" />
              </span>
            </div>
            <span className="mt-1 block text-right text-[7px] font-semibold text-[#5881a7]">Open</span>
          </div>
          <div className="rounded-lg border border-[#dfe4ea] p-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[9px] font-bold text-[#263958]">Progress</span>
              <span className="text-[9px] font-bold text-[#263958]">68%</span>
            </div>
            <div className="mt-4 h-1.5 rounded-full bg-[#e9edf1]">
              <div className="h-full w-2/3 rounded-full bg-[#2e79ad]" />
            </div>
            <span className="mt-3 flex items-center gap-1 text-[7px] font-semibold text-[#73849a]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#5bbc82]" /> On track
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function VirtualBoardPreview() {
  const experts = [
    {
      initials: "SS",
      name: "Simon Sinek",
      method: "Start With Why",
      skills: ["Leadership", "Purpose", "Culture"],
    },
    {
      initials: "AH",
      name: "Alex Hormozi",
      method: "$100M Offers",
      skills: ["Offer design", "Pricing", "Growth"],
    },
    {
      initials: "AD",
      name: "April Dunford",
      method: "Obviously Awesome",
      skills: ["Positioning", "Market category", "Messaging"],
    },
  ];

  return (
    <div
      className="flex min-h-[410px] flex-col overflow-hidden rounded-xl border border-[#dfbd98] bg-white text-left shadow-[0_20px_50px_-34px_rgba(86,52,20,0.45)]"
      aria-hidden
    >
      <div className="border-b border-[#ead6c0] bg-[#fffaf5] p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#e4c7a8] bg-white text-[#a85b1a]">
              <LayoutDashboard className="h-4 w-4" />
            </span>
            <p className="truncate text-base font-semibold text-[#17283a]">Growth Strategy Board</p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 text-xs text-[#607393]">
            <Clock className="h-3.5 w-3.5" />
            1d ago
          </div>
        </div>
      </div>

      <div className="flex-1 p-5">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div className="flex -space-x-2">
            {experts.map((expert, index) => (
              <span
                key={expert.name}
                className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white ring-1 ring-[#cf9c6a]"
                style={{ backgroundColor: ["#244d6f", "#a96640", "#5e7188"][index] }}
              >
                {expert.initials}
              </span>
            ))}
          </div>
          <span className="rounded-md bg-[#fff3e5] px-2.5 py-1 text-xs font-semibold text-[#a05214]">
            3 Experts
          </span>
        </div>

        <div className="space-y-4">
          {experts.map((expert, index) => (
            <div key={expert.name} className="flex gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[#e1b889] bg-[#fff8f0] text-[11px] font-semibold text-[#b25d17]">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="font-medium text-[#17283a]">{expert.name}</span>
                  <span className="text-xs text-[#71839b]">{expert.method}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {expert.skills.map((skill) => (
                    <span key={skill} className="rounded-md bg-[#eaf0f5] px-2 py-0.5 text-[11px] text-[#67809a]">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-[#ead6c0] bg-[#fffaf5] px-5 py-3.5 text-sm font-medium text-[#a05214]">
        Open Expert Board
        <ArrowRight className="h-4 w-4" />
      </div>
    </div>
  );
}

function PlatformLayerCard({
  layer,
  title,
  description,
  badge,
  preview,
}: {
  layer: string;
  title: string;
  description: string;
  badge: string;
  preview: React.ReactNode;
}) {
  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-[#e0e5eb] bg-white shadow-[0_16px_32px_-28px_rgba(12,35,69,0.55)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_38px_-25px_rgba(12,35,69,0.45)]">
      <div className="h-52 md:h-60">{preview}</div>
      <div className="flex flex-1 flex-col p-6 sm:p-7">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#274d79]">Layer {layer}</p>
        <h3 className="mt-4 font-serif text-2xl font-bold leading-tight text-[#0f1f43] sm:text-[1.75rem]">{title}</h3>
        <p className="mt-4 flex-1 text-sm leading-6 text-[#607393]">{description}</p>
        <span className="mt-6 w-fit rounded-full bg-[#edf2f7] px-3.5 py-1.5 text-xs font-bold text-[#31567e]">{badge}</span>
      </div>
    </article>
  );
}

const Home = () => {
  const navigate = useNavigate();
  const { user, isAnonymous } = useAuth();
  const { toast } = useToast();

  const [thinkingMethods, setThinkingMethods] = useState<ThinkingMethod[]>([]);
  const [publishedLensCount, setPublishedLensCount] = useState<number | null>(null);
  const [goalPrompt, setGoalPrompt] = useState("");
  const [routerLoading, setRouterLoading] = useState(false);
  const [routerError, setRouterError] = useState("");
  const [selectedShowcaseApp, setSelectedShowcaseApp] = useState<ShowcasePreview | null>(null);
  const [selectedHomepageTemplate, setSelectedHomepageTemplate] = useState<PublicAppTemplate | null>(null);
  const [homepageSource, setHomepageSource] = useState<"hardcoded" | "catalog">("hardcoded");
  const [homepageTemplates, setHomepageTemplates] = useState<PublicAppTemplate[]>([]);
  const [homepageCatalogLoading, setHomepageCatalogLoading] = useState(true);

  // Fetch expert lenses with visibility filtering
  useEffect(() => {
    const fetchThinkingMethods = async () => {
      const [{ count }, { data: methodsData }] = await Promise.all([
        supabase.from("thinking_methods").select("id", { count: "exact", head: true }).eq("status", "published"),
        supabase.from("thinking_methods").select(THINKING_METHOD_HOME_PROJECTION).order("order_index", { ascending: true }),
      ]);

      if (typeof count === "number") {
        setPublishedLensCount(count);
      }

      if (!methodsData) {
        setThinkingMethods([]);
        if (typeof count !== "number") {
          setPublishedLensCount(0);
        }
        return;
      }

      let isAdmin = false;
      let userEmail = "";

      if (user) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("is_admin, email")
          .eq("user_id", user.id)
          .single();

        isAdmin = profileData?.is_admin || false;
        userEmail = profileData?.email || "";
      }

      const filteredMethods = methodsData.filter((method: ThinkingMethodRow) => {
        const shouldShow = (() => {
          switch (method.status) {
            case "draft":
              return false;
            case "admins":
              return isAdmin;
            case "author":
              return isAdmin || (method.author_email && method.author_email.toLowerCase() === userEmail.toLowerCase());
            case "published":
              return true;
            default:
              return false;
          }
        })();
        return shouldShow;
      });

      // Fetch JSON_PROPERTIES attributes for industry info
      const methodIds = filteredMethods.map((m: ThinkingMethodRow) => m.id);
      const { data: attrsData } = await loadLensJsonProperties(methodIds);

      const propsMap: Record<string, { industry?: string; header?: string; teaser?: string; skills?: string[] }> = {};
      if (attrsData) {
        for (const attr of attrsData) {
          try {
            const parsed = JSON.parse(attr.attribute_value);
            propsMap[attr.thinking_method_id] = {
              industry: parsed.industry || undefined,
              header: parsed.header || undefined,
              teaser: parsed.teaser || undefined,
              skills: Array.isArray(parsed.skills) ? parsed.skills : undefined,
            };
          } catch {
            // Ignore malformed lens metadata for homepage display.
          }
        }
      }

      const methodsWithProps = filteredMethods.map((m: ThinkingMethodRow) => ({
        ...m,
        ...propsMap[m.id],
      }));

      setThinkingMethods(methodsWithProps);

      if (typeof count !== "number") {
        setPublishedLensCount(methodsData.filter((method: ThinkingMethodRow) => method.status === "published").length);
      }
    };

    void fetchThinkingMethods();
  }, [user]);

  useEffect(() => {
    let active = true;
    void loadHomepageTemplateGallery()
      .then((gallery) => {
        if (!active) return;
        setHomepageSource(gallery.homepage_source);
        setHomepageTemplates(gallery.templates);
      })
      .catch(() => {
        // Keep the existing gallery as the public fallback on API failure.
        if (active) setHomepageSource("hardcoded");
      })
      .finally(() => {
        if (active) setHomepageCatalogLoading(false);
      });
    return () => { active = false; };
  }, []);

  const publishedLensCountLabel = publishedLensCount === null ? "..." : String(publishedLensCount);

  const features = [
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Licensed Digital Twins",
      description: "Engage with AI replicas trained on expert methodologies and thinking patterns",
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Action Artifacts",
      description: "Get practical deliverables like briefs, plans, and strategies",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Always Cited",
      description: "Every response includes source attribution and methodology transparency",
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Multi-Lens Compare",
      description: "See how different experts would approach the same challenge simultaneously",
    },
  ];

  const handleExploreLenses = () => {
    navigate("/lenses");
  };

  const handleBuildOs = async (event: React.FormEvent<HTMLFormElement>, source: "hero" | "footer") => {
    event.preventDefault();
    if (!user || isAnonymous) {
      navigate("/beta_access");
      return;
    }
    const userOutcome = goalPrompt.trim();
    if (!userOutcome) {
      setRouterError("Enter the outcome you want Applyt to build around.");
      return;
    }
    trackEvent("home_build_os_started", {
      source,
      has_outcome: true,
    });
    setRouterLoading(true);
    setRouterError("");
    try {
      const { data, error } = await supabase.functions.invoke("gate-router", {
        body: { action: "start", userOutcome },
      });
      if (error) throw new Error(await gateRouterErrorMessage(error, "Unable to start the Wizard."));
      if (data?.error) throw new Error(data.error);
      navigate(getWizardResumeNavigation(data));
    } catch (error) {
      setRouterError(error instanceof Error ? error.message : "Unable to start the Wizard.");
    } finally {
      setRouterLoading(false);
    }
  };

  const renderBuildOsPrompt = (source: "hero" | "footer") => (
    <form className="mx-auto w-full max-w-3xl" onSubmit={(event) => handleBuildOs(event, source)}>
      <label htmlFor={`${source}-os-outcome`} className="sr-only">
        Describe the app you need
      </label>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start sm:gap-4">
        <div className="min-w-0">
          <div className="rounded-2xl border border-primary/10 bg-card shadow-[0_10px_40px_-15px_hsl(var(--primary)/0.18)]">
            <Input
              id={`${source}-os-outcome`}
              value={goalPrompt}
              onChange={(event) => { setGoalPrompt(event.target.value); setRouterError(""); }}
              placeholder="Describe the app you need"
              className="h-12 w-full border-none bg-transparent px-4 text-sm text-foreground shadow-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 sm:h-14 sm:px-6 sm:text-lg"
            />
          </div>
          <div className="mt-3 flex w-full flex-wrap justify-center gap-2 sm:mt-4 sm:gap-2.5">
            {outcomeSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => { setGoalPrompt(suggestion); setRouterError(""); }}
                className="shrink-0 rounded-full border border-primary/15 bg-card/90 px-3.5 py-1.5 text-sm font-medium text-foreground shadow-sm transition-colors hover:border-primary/35 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 sm:px-4 sm:py-2"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
        <button
          type="submit"
          disabled={routerLoading}
          aria-label={routerLoading ? "Building your App…" : "Build my App"}
          className="group relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm transition-all hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-80 sm:h-14 sm:w-14"
        >
          {routerLoading ? (
            /* Lovable-style spinning arc */
            <span className="h-5 w-5 rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground animate-spin sm:h-6 sm:w-6" />
          ) : (
            <ArrowRight
              className="h-5 w-5 -rotate-90 transition-transform duration-200 group-hover:-translate-y-0.5 sm:h-6 sm:w-6"
              strokeWidth={2.5}
            />
          )}
        </button>
      </div>
      <p className="mt-6 text-center text-sm text-muted-foreground sm:mt-8">
        Not sure?{" "}
        <Link
          to="/lenses"
          className="font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          Ask an expert first →
        </Link>
      </p>
      {routerError && <p className="mt-4 text-center text-sm text-destructive">{routerError}</p>}
    </form>
  );

  const handleMethodClick = (methodId: string) => {
    trackEvent("prompt_started", {
      lens_id: methodId,
      lens_type: "single",
      source: "home",
    });
    navigate(`/chat/new?method=${methodId}`);
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Public site — show the full home to everyone (anonymous + signed-in).
  return (
    <>
      <Seo
        title="Apply Proven Expert Methods to Your Startup | Applyt"
        description="Applyt applies proven expert methods to your specific situation, turning your context into strategies, plans, and finished work without hiring an expert."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Applyt",
            url: "https://applyt.ai/",
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Applyt",
            url: "https://applyt.ai/",
          },
        ]}
      />
      <div className="bg-background text-foreground font-sans">
        {/* Hero viewport */}
        <div className="flex min-h-[100svh] flex-col">
          {/* Navigation */}
          <Header />

          {/* Hero Section */}
          <section className="relative z-10 flex flex-1 flex-col justify-center px-4 py-4 sm:px-6 sm:py-6 lg:py-8">
            <div className="mx-auto w-full max-w-6xl text-center">
              <div className="mx-auto max-w-5xl">
                <h1
                  className="mx-auto mb-3 font-bold leading-[1.03] tracking-normal text-foreground sm:mb-4"
                  style={HOME_H1_STYLE}
                >
                  {/* <span className="block font-serif text-3xl text-[#0f1f43] sm:text-4xl lg:text-5xl">
                    You know your goal.
                  </span> */}
                  <span className="mt-2 block text-primary md:mt-3">
                    Build the Operations App your company runs on.
                  </span>
                </h1>
              </div>

              <p className="mx-auto max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg font-sans">
                Every app is tailored to a specific task using proven expert methods, with stages, roles, workflows, KPIs, and dashboards.
              </p>
              <div className="mt-5 w-full sm:mt-7 lg:mt-9 xl:mt-10">{renderBuildOsPrompt("hero")}</div>
            </div>
          </section>
        </div>

        {/* Product demo */}
        <section
          aria-labelledby="apps-gallery-label"
          className="relative z-10 overflow-hidden bg-[#071426] px-4 py-16 sm:px-6 sm:py-20 lg:py-24"
        >
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-[radial-gradient(circle_at_top,rgba(54,127,166,0.24),transparent_68%)]"
            aria-hidden
          />
          <div className="relative mx-auto max-w-[1400px]">
            <div className="mb-10 text-center sm:mb-14">
              <h2
                id="apps-gallery-label"
                className="font-serif text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl"
                style={HOME_H2_STYLE}
              >
                App Templates that Scale Business
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
              {homepageSource === "catalog" ? (
                homepageCatalogLoading ? (
                  Array.from({ length: 6 }, (_, index) => (
                    <div key={index} className="aspect-[16/10] animate-pulse rounded-xl border border-white/10 bg-white/10 sm:rounded-2xl" aria-label="Loading Template preview" />
                  ))
                ) : homepageTemplates.length === 0 ? (
                  <div className="rounded-xl border border-white/10 bg-white/5 px-5 py-6 text-sm text-white/70 sm:rounded-2xl">No Template previews are available yet.</div>
                ) : homepageTemplates.map((template) => template.preview_image_url ? (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => setSelectedHomepageTemplate(template)}
                    className="group overflow-hidden rounded-xl border border-white/10 bg-black/60 text-left shadow-[0_20px_60px_-24px_rgba(0,0,0,0.9)] transition duration-200 hover:-translate-y-0.5 hover:border-white/30 hover:shadow-[0_24px_64px_-24px_rgba(0,0,0,0.95)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#071426] sm:rounded-2xl"
                  >
                    <img src={template.preview_image_url} alt={`${template.name} App preview`} loading="lazy" className="block h-full w-full object-cover object-top transition duration-200 group-hover:scale-[1.01]" />
                    <span className="sr-only">Open larger preview of {template.name}</span>
                  </button>
                ) : (
                  <div key={template.id} className="flex aspect-[16/10] items-end rounded-xl border border-white/10 bg-white/5 p-4 text-sm font-medium text-white/70 sm:rounded-2xl">{template.name}</div>
                ))
              ) : homeAppShowcase.map((app) => (
                <button
                  key={app.src}
                  type="button"
                  onClick={() => setSelectedShowcaseApp(app)}
                  className="group overflow-hidden rounded-xl border border-white/10 bg-black/60 text-left shadow-[0_20px_60px_-24px_rgba(0,0,0,0.9)] transition duration-200 hover:-translate-y-0.5 hover:border-white/30 hover:shadow-[0_24px_64px_-24px_rgba(0,0,0,0.95)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#071426] sm:rounded-2xl"
                >
                  <img src={app.src} alt={app.alt} loading="lazy" className="block h-full w-full object-cover object-top transition duration-200 group-hover:scale-[1.01]" />
                  <span className="sr-only">Open larger preview of {app.alt}</span>
                </button>
              ))}
            </div>
            <div className="mt-10 flex justify-center sm:mt-12">
              <Link
                to="/templates"
                className="inline-flex items-center gap-2 text-base font-medium text-white/80 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#071426]"
              >
                Explore all Templates
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        <TemplatePreviewDialog
          template={selectedHomepageTemplate}
          actionsDisabled={false}
          creating={false}
          onOpenChange={(open) => {
            if (!open) setSelectedHomepageTemplate(null);
          }}
          onCopyLink={() => {
            if (!selectedHomepageTemplate) return;
            void copyTemplatePreviewLink(selectedHomepageTemplate.id)
              .then(() => toast({
                title: "Template link copied",
                description: `Share ${selectedHomepageTemplate.name} from the Template catalog.`,
              }))
              .catch(() => toast({
                title: "Could not copy link",
                description: "Copy the link from your browser address bar instead.",
                variant: "destructive",
              }));
          }}
          onCreate={() => {
            if (!selectedHomepageTemplate) return;
            const continuation = templateContinuationPath(selectedHomepageTemplate.id, createUuid());
            rememberAppTemplateContinuation(continuation);
            if (!user || isAnonymous) {
              navigate(`/beta_access?redirect=${encodeURIComponent(continuation)}`, { replace: true });
              return;
            }
            navigate(continuation, { replace: true });
          }}
        />

        <Dialog
          open={selectedShowcaseApp !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedShowcaseApp(null);
          }}
        >
          <DialogContent className="w-[calc(100vw-1rem)] max-w-[1800px] border-white/15 bg-[#071426] p-2 text-white sm:p-3">
            <DialogHeader className="sr-only">
              <DialogTitle>{selectedShowcaseApp?.alt}</DialogTitle>
              <DialogDescription>Large preview of an Applyt app dashboard.</DialogDescription>
            </DialogHeader>
            {selectedShowcaseApp?.src ? (
              <img
                src={selectedShowcaseApp.src}
                alt={selectedShowcaseApp.alt}
                className="max-h-[90svh] w-full rounded-md object-contain"
              />
            ) : selectedShowcaseApp ? (
              <div className="flex min-h-72 flex-col items-center justify-center gap-3 rounded-md bg-white/5 text-sm text-white/75">
                <ImageOff className="h-6 w-6" aria-hidden="true" />
                Preview image
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Platform: Three connected App layers */}
        <section className="relative z-10 bg-[#fbfcfe] px-4 py-20 sm:px-6 lg:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-12 max-w-4xl text-center lg:mb-14">
              <p className="mb-6 inline-flex items-center gap-2 rounded-md border border-[#dfbd98] bg-[#fff7ed] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#a05214]">
                <Network className="h-3.5 w-3.5" />
                Our Platform
              </p>
              <h2 className="font-serif text-3xl font-bold leading-tight text-[#0f1f43] sm:text-4xl lg:text-5xl" style={HOME_H2_STYLE}>
                Three AI Layers Behind Applyt Apps
              </h2>
              <p className="mx-auto mt-5 max-w-3xl text-base leading-7 text-[#607393] sm:text-lg sm:leading-8">
                Each Applyt App combines three connected layers: your business context, an expert-designed operating system, and the execution workspace where your team runs it.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              <PlatformLayerCard
                layer="01"
                title="Context Map"
                description="Applyt structures the information that matters: your situation, constraints, priorities, risks, assets, and success criteria."
                badge="Context-aware foundation"
                preview={<ContextMapPreview />}
              />
              <PlatformLayerCard
                layer="02"
                title="Generate"
                description="Generates an App from proven expert methods: requests, decision rules, workflows, checkpoints, and priorities."
                badge="Method-backed operating logic"
                preview={<OperatingBlueprintPreview />}
              />
              <PlatformLayerCard
                layer="03"
                title="Run and refine"
                description="Run a live internal operations workspace where your team manages actions, decisions, outputs, accountability, and progress in one place."
                badge="Built for execution"
                preview={<ExecutionWorkspacePreview />}
              />
            </div>
          </div>
        </section>

        <section id="lenses" className="relative z-10 py-20 px-6 bg-card">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p className="mb-3 inline-flex items-center gap-2 rounded-md border border-[#dfbd98] bg-[#fff7ed] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#a05214]">
                <Brain className="h-3.5 w-3.5" />
                Our Experts
              </p>
              <h2 className="mb-4 font-serif text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl" style={HOME_H2_STYLE}>
                App built on proven methods and expertise.
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto">
                Each App captures how an expert truly thinks, makes decisions, solves problems, and approaches work,
                all structured for execution.
              </p>
            </div>

            <div
              className="expert-marquee relative overflow-hidden motion-reduce:overflow-x-auto"
              aria-label="Expert methods"
              role="region"
            >
              {/* Fade edges */}
              <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-card to-transparent z-10 pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-card to-transparent z-10 pointer-events-none" />

              <div
                className="expert-marquee-track flex w-max"
                style={{ animationDuration: `${expertMarqueeDurationSeconds(thinkingMethods.length)}s` }}
              >
                {[0, 1].map((copy) => (
                  <div
                    key={copy}
                    className={`flex shrink-0 gap-6 pr-6 ${copy === 1 ? "motion-reduce:hidden" : ""}`}
                    aria-hidden={copy === 1 ? true : undefined}
                  >
                    {thinkingMethods.map((method) => (
                      <div
                        key={method.id}
                        className="group border border-border/50 rounded-2xl overflow-hidden transition-all duration-200 hover:border-primary/30 hover:-translate-y-0.5 cursor-pointer flex flex-col w-[300px] sm:w-[480px] flex-shrink-0"
                        onClick={() => handleMethodClick(method.id)}
                        onKeyDown={copy === 0 ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleMethodClick(method.id);
                          }
                        } : undefined}
                        role={copy === 0 ? "button" : undefined}
                        tabIndex={copy === 0 ? 0 : -1}
                      >
                        {/* Header */}
                        <div className="px-7 py-5 bg-muted/50 border-b border-border/50 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-primary/20 to-primary/10 border border-border">
                              {method.icon_url ? (
                                <img src={method.icon_url} alt={method.author} className="h-full w-full object-cover" />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center text-primary font-semibold text-sm">
                                  {getInitials(method.author)}
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="font-semibold text-sm">{method.author}</div>
                              <div className="text-xs text-muted-foreground">Author, {method.name}</div>
                            </div>
                          </div>
                        </div>

                        {/* Body */}
                        <div className="px-7 py-6 flex-1 flex flex-col">
                          <h3
                            className="text-[22px] font-bold mb-2 leading-tight"
                            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                          >
                            {method.header || method.name}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed mb-5 line-clamp-4 flex-1">
                            {method.teaser || method.bio}
                          </p>
                          {method.skills && method.skills.length > 0 && (
                            <div className="flex gap-2 flex-wrap mt-auto">
                              {method.skills.slice(0, 3).map((skill, index) => (
                                <span
                                  key={index}
                                  className="text-xs px-3 py-1.5 rounded-md bg-muted text-muted-foreground font-medium"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* CTA */}
                        <div
                          className="block text-center py-3.5 bg-foreground text-background text-sm font-semibold border-t border-border/50 hover:bg-foreground/90 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMethodClick(method.id);
                          }}
                        >
                          Use This Method
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center mt-12">
              <button
                onClick={handleExploreLenses}
                className="text-primary hover:text-primary/80 transition-colors duration-200 flex items-center space-x-2 mx-auto"
              >
                <span>Explore all experts and choose the one that fits </span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
        {/* Stats Banner */}
        <section className="relative z-10 py-12 px-6 bg-foreground">
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: publishedLensCountLabel, label: "Published experts and methods" },
              { value: "12x", label: "Faster and more effective than a consultant" },
              { value: "85%", label: "Users produce a deliverable on first session" },
              { value: "9 min", label: "Average time to finished App" },
            ].map((stat, i) => (
              <div key={i}>
                <p
                  className="text-3xl md:text-4xl font-bold text-primary-foreground mb-1"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {stat.value}
                </p>
                <p className="text-sm text-primary-foreground/70">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Virtual Expert Boards */}
        <section className="relative z-10 overflow-hidden border-y border-[#dce4ee] bg-[#f4f7fb] px-6 py-20 lg:py-24">
          <div className="pointer-events-none absolute -right-32 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-primary/5 blur-3xl" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(420px,1.1fr)] lg:gap-16">
            <div className="max-w-xl">
              <p className="mb-3 inline-flex items-center gap-2 rounded-md border border-[#dfbd98] bg-[#fff7ed] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#a05214]">
                <Users className="h-3.5 w-3.5" />
                Executive Workspace
              </p>
              <h2 className="font-serif text-3xl font-bold leading-tight text-[#0f1f43] sm:text-4xl lg:text-5xl" style={HOME_H2_STYLE}>
                Expert Boards
              </h2>
              <p className="mt-5 text-lg leading-8 text-muted-foreground">
                Each Board is packed with high-profile, real-world business mentors, authors, and industry pioneers.
                Put their proven methods to work on your tasks, with each Expert contributing from a distinct perspective.
              </p>

              <div className="mt-7 space-y-4">
                {[
                  "Choose the Experts that fit your challenge.",
                  "Let them talk to each other and advise you.",
                  "Keep the Board as a saved workspace for your goal.",
                ].map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <CheckCircle className="h-4 w-4" />
                    </span>
                    <p className="text-base leading-6 text-[#405572]">{benefit}</p>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                size="lg"
                className="mt-8 gap-2 rounded-xl px-6 font-semibold"
                onClick={() => navigate("/expert-boards")}
              >
                <Users className="h-4 w-4" />
                Create Your Virtual Board
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>

            <VirtualBoardPreview />
          </div>
        </section>


        <section
          className="relative z-10 px-6 pt-24 pb-20 border-t"
          style={{
            background: "linear-gradient(180deg, #FFF8EE 0%, #FFFFFF 100%)",
            borderColor: "#E8D8C3",
          }}
        >
          <div className="absolute inset-x-0 top-0 pointer-events-none">
            <div
              className="mx-auto h-px max-w-5xl"
              style={{
                background:
                  "linear-gradient(90deg, rgba(196,136,58,0) 0%, rgba(196,136,58,0.75) 50%, rgba(196,136,58,0) 100%)",
              }}
            />
          </div>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2
                className="mb-4 font-serif text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl"
                style={{ ...HOME_H2_STYLE, color: "#1A5276" }}
              >
                Why Applyt?
              </h2>
              <p className="text-xl text-muted-foreground">The most advanced Operations App Builder.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-16">
              {[
                {
                  icon: <Zap className="w-11 h-11 text-primary" />,
                  title: "Use expert methods in the moment you need them",
                  desc: "No need to search through books, notes, or saved content to figure out the next step.",
                },
                {
                  icon: <Puzzle className="w-11 h-11 text-primary" />,
                  title: "Integrated into your workspace",
                  desc: "Use Applyt inside the tools you or your team already work in, so expert guidance becomes part of daily execution.",
                },
                {
                  icon: <Brain className="w-11 h-11 text-primary" />,
                  title: "Built for your company's operations, not generic Apps",
                  desc: "Applyt applies the roles, objectives, workflows, and metrics your company needs.",
                },
              ].map((item, i) => (
                <Card key={i} className="bg-secondary/30 border-border text-center">
                  <CardContent className="p-6">
                    <div className="mb-5 flex justify-center">
                      <div className="flex h-24 w-24 items-center justify-center rounded-[28px] bg-primary/10">
                        {item.icon}
                      </div>
                    </div>
                    <h3 className="font-semibold text-lg uppercase tracking-wide mb-3">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 py-20 px-6 bg-muted/30">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="mb-6 font-serif text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl" style={HOME_H2_STYLE}>
              Start Building Your Operations App
            </h2>

            {renderBuildOsPrompt("footer")}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Home;
